import { useMemo, useState } from "react"
import {
	IconCheckDoubleOutline18 as CheckCheckIcon,
	IconCloudUploadOutline18 as UploadCloudIcon,
} from "nucleo-ui-outline-18"
import { parse } from "papaparse"

import { Button } from "@productlane/ui"

import { ContactStep } from "../../csv-import/steps/contact-step"
import { HeaderStep } from "../../csv-import/steps/header-step"
import { UploadStep } from "../../csv-import/steps/upload-step"
import { ContactsImageUrlStep } from "./contacts-image-url-step"
import { ContactsPreviewDialog } from "./contacts-preview-dialog"
import { ContactsSubscribedStep } from "./contacts-subscribed-step"

export function ContactsCsvUploadWizard() {
	const [step, setStep] = useState(0)
	const [data, setData] = useState<string[][]>([[]])
	const [hasHeader, setHasHeader] = useState(true)

	const [contactEmailIndex, setContactEmailIndex] = useState<number>(-1)
	const [contactNameIndex, setContactNameIndex] = useState<number>(-1)
	const [imageUrlIndex, setImageUrlIndex] = useState<number>(-1)
	const [subscribedIndex, setSubscribedIndex] = useState<number>(-1)
	const [subscribeUnmapped, setSubscribeUnmapped] = useState(false)

	const [previewDialog, setPreviewDialog] = useState(false)

	const sortedData = useMemo(() => {
		const result: string[][] = []

		if (!data[0]) {
			return [[]]
		}

		for (let i = 0; i < data[0].length; i++) {
			const newColumn: string[] = []
			for (const row of data) {
				newColumn.push(row[i] ?? "")
			}
			result.push(newColumn)
		}

		return result
	}, [data])

	const handleFileInput = (file: File) => {
		setStep(1)
		parse<string[], File>(file, {
			complete: (result) => {
				setData(result.data)
			},
			skipEmptyLines: true,
		})
	}

	const reset = () => {
		setData([[]])
		setHasHeader(true)
		setStep(0)
		setContactEmailIndex(-1)
		setContactNameIndex(-1)
		setImageUrlIndex(-1)
		setSubscribedIndex(-1)
		setPreviewDialog(false)
	}

	return (
		<div>
			{step === 0 && <UploadStep onSuccess={handleFileInput} />}
			{step === 1 && (
				<HeaderStep
					data={data}
					hasHeader={hasHeader}
					cancel={() => setStep(0)}
					select={(hasHeader) => {
						setHasHeader(hasHeader)
						setStep(2)
					}}
				/>
			)}
			{step === 2 && (
				<ContactStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					back={() => setStep(1)}
					next={() => setStep(3)}
					contactEmailIndex={contactEmailIndex}
					contactNameIndex={contactNameIndex}
					setContactEmailIndex={setContactEmailIndex}
					setContactNameIndex={setContactNameIndex}
				/>
			)}
			{step === 3 && (
				<ContactsImageUrlStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					imageUrlIndex={imageUrlIndex}
					setImageUrlIndex={setImageUrlIndex}
					back={() => setStep(2)}
					next={() => setStep(4)}
				/>
			)}
			{step === 4 && (
				<ContactsSubscribedStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					subscribedIndex={subscribedIndex}
					setSubscribedIndex={setSubscribedIndex}
					subscribeUnmapped={subscribeUnmapped}
					setSubscribeUnmapped={setSubscribeUnmapped}
					back={() => setStep(3)}
					next={() => setPreviewDialog(true)}
				/>
			)}

			<ContactsPreviewDialog
				isOpen={previewDialog}
				onOpenChange={() => setPreviewDialog(false)}
				hasHeaders={hasHeader}
				sortedData={sortedData}
				contactEmailIndex={contactEmailIndex}
				contactNameIndex={contactNameIndex}
				imageUrlIndex={imageUrlIndex}
				subscribedIndex={subscribedIndex}
				subscribeUnmapped={subscribeUnmapped}
				onSuccess={() => setStep(5)}
			/>

			{step === 5 && (
				<div className="flex items-center justify-center p-8">
					<div className="flex flex-col items-center gap-2">
						<CheckCheckIcon className="h-8 w-8 text-green-500" />
						<div className="text-secondary">Contacts successfully imported</div>
						<Button
							className="text-secondary"
							size="sm"
							variant="ghost"
							Icon={UploadCloudIcon}
							onClick={reset}
						>
							Upload another file
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}
